import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { mysqlNow, toMysqlDatetime } from '../utils/mysqlDatetime';

const CartContext = createContext(null);
const CART_KEY = 'oh_cart_v1';
let lineSeq = 0;

function nextLineId() {
  lineSeq += 1;
  return `line-${lineSeq}`;
}

const initialState = {
  items: [],
  pedidoMeta: {
    transportadora: '',
    transportadora_otra: '',
    horario_recibo: '',
    tipo_documento: 'remision',
    forma_pago: 'contado',
  },
  cliente: {
    cliente_id: null,
    sucursal_id: null,
    sucursal_codigo: '',
    nombre: '',
    documento: '',
    telefono: '',
    telefono2: '',
    ciudad: '',
    zona: '',
    direccion: '',
    email: '',
    notas: '',
    vendedor: '',
    vendedor_id: null,
    visita_id: null,
    visita_inicio: null,
    visita_fin: null,
  },
};

function loadPersistedCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return initialState;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return initialState;
    const items = Array.isArray(data.items) ? data.items : [];
    let maxSeq = 0;
    items.forEach((it) => {
      const m = String(it.lineId || '').match(/line-(\d+)/);
      if (m) maxSeq = Math.max(maxSeq, Number(m[1]) || 0);
    });
    lineSeq = maxSeq;
    const cliente = { ...initialState.cliente, ...(data.cliente || {}) };
    const pedidoMeta = { ...initialState.pedidoMeta, ...(data.pedidoMeta || {}) };
    if (!pedidoMeta.tipo_documento) pedidoMeta.tipo_documento = 'remision';
    if (!pedidoMeta.forma_pago) pedidoMeta.forma_pago = 'contado';
    // No reabrir visitas viejas al restaurar (GPS / sync raro)
    return {
      items,
      pedidoMeta,
      cliente: {
        ...cliente,
        visita_id: null,
        visita_inicio: null,
        visita_fin: null,
      },
    };
  } catch {
    return initialState;
  }
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const p = action.product;
      const obs = action.observaciones || '';
      const qty = action.cantidad || 1;
      const opcionId = action.opcion_id || null;
      const existing = state.items.find(
        (i) =>
          i.codigo === p.codigo &&
          (i.opcion_id || null) === opcionId &&
          (i.observaciones || '') === obs
      );
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i.lineId === existing.lineId ? { ...i, cantidad: i.cantidad + qty } : i
          ),
        };
      }
      return {
        ...state,
        items: [
          ...state.items,
          {
            lineId: nextLineId(),
            producto_id: p.id,
            codigo: p.codigo,
            nombre: p.nombre,
            precio_unitario: Number(p.precio),
            imagen: action.imagen || p.imagen,
            cantidad: qty,
            observaciones: obs,
            opcion_id: opcionId,
          },
        ],
      };
    }
    case 'SET_QTY': {
      const qty = Math.max(0, action.qty);
      if (qty === 0) {
        return {
          ...state,
          items: state.items.filter((i) => i.lineId !== action.lineId),
        };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          i.lineId === action.lineId ? { ...i, cantidad: qty } : i
        ),
      };
    }
    case 'SET_OBS':
      return {
        ...state,
        items: state.items.map((i) =>
          i.lineId === action.lineId ? { ...i, observaciones: action.observaciones } : i
        ),
      };
    case 'REMOVE':
      return {
        ...state,
        items: state.items.filter((i) => i.lineId !== action.lineId),
      };
    case 'REMOVE_BY_CODIGO':
      return {
        ...state,
        items: state.items.filter((i) => String(i.codigo) !== String(action.codigo)),
      };
    case 'CLEAR':
      return {
        ...state,
        items: [],
        pedidoMeta: { ...initialState.pedidoMeta },
        cliente: {
          ...initialState.cliente,
          vendedor: state.cliente.vendedor,
          vendedor_id: state.cliente.vendedor_id,
        },
      };
    case 'CLEAR_CLIENTE':
      return {
        ...state,
        cliente: {
          ...initialState.cliente,
          vendedor: state.cliente.vendedor,
          vendedor_id: state.cliente.vendedor_id,
        },
      };
    case 'SET_CLIENTE':
      return { ...state, cliente: { ...state.cliente, ...action.data } };
    case 'SELECT_SUCURSAL': {
      const s = action.sucursal;
      const v = action.visita;
      const fallbackInicio = mysqlNow();
      const esSede = Boolean(s.es_sede || s.padre_id);
      const sedeEtiqueta = s.sede_etiqueta || s.etiqueta || s.direccion || 'Sede';
      const nombre =
        s.display_nombre ||
        (esSede && (s.padre_nombre || s.nombre)
          ? `${s.padre_nombre || s.nombre} · ${sedeEtiqueta}`
          : s.nombre);
      return {
        ...state,
        cliente: {
          ...state.cliente,
          cliente_id: null,
          sucursal_id: s.id,
          sucursal_codigo: s.codigo != null ? String(s.codigo) : '',
          nombre,
          documento: s.documento || '',
          telefono: s.telefono1 || s.telefono2 || s.telefono || '',
          telefono2: s.telefono2 || '',
          ciudad: s.ciudad || '',
          zona: s.zona || '',
          direccion: s.direccion || '',
          email: s.email || '',
          vendedor: s.vendedor_nombre || state.cliente.vendedor,
          vendedor_id: s.vendedor_id || state.cliente.vendedor_id || null,
          es_sede: esSede,
          padre_codigo: s.padre_codigo || '',
          sede_etiqueta: esSede ? sedeEtiqueta : '',
          visita_id: v !== undefined ? (v?.id ?? null) : state.cliente.visita_id,
          // Si aún no hay visita del API, arranca el reloj local desde ya.
          visita_inicio:
            v !== undefined
              ? (toMysqlDatetime(v?.inicio) || fallbackInicio)
              : (toMysqlDatetime(state.cliente.visita_inicio) || fallbackInicio),
          visita_fin: v !== undefined ? null : state.cliente.visita_fin,
        },
      };
    }
    case 'SET_PEDIDO_META':
      return { ...state, pedidoMeta: { ...state.pedidoMeta, ...action.data } };
    case 'SET_VISITA_FIN':
      return {
        ...state,
        cliente: { ...state.cliente, visita_fin: action.fin || mysqlNow() },
      };
    case 'RECONCILE_PRODUCTS': {
      const byCodigo = action.byCodigo || {};
      let changed = false;
      const removed = [];
      const priceChanges = [];
      const items = [];
      for (const it of state.items) {
        const fresh = byCodigo[String(it.codigo)];
        if (!fresh) {
          // Producto aún no en el lote cargado: conservar hasta confirmación del servidor
          items.push(it);
          continue;
        }
        if (fresh.__unavailable) {
          removed.push(it.codigo);
          changed = true;
          continue;
        }
        const activo = fresh.activo == null ? true : Number(fresh.activo) !== 0;
        const visible = fresh.visible_app == null ? true : Number(fresh.visible_app) !== 0;
        const enStock = fresh.en_stock == null ? true : Number(fresh.en_stock) !== 0;
        if (!activo || !visible || !enStock) {
          removed.push(it.codigo);
          changed = true;
          continue;
        }
        const nuevoPrecio = Number(fresh.precio);
        if (Number.isFinite(nuevoPrecio) && nuevoPrecio > 0 && nuevoPrecio !== Number(it.precio_unitario)) {
          priceChanges.push({
            codigo: it.codigo,
            de: Number(it.precio_unitario),
            a: nuevoPrecio,
          });
          items.push({
            ...it,
            nombre: fresh.nombre || it.nombre,
            precio_unitario: nuevoPrecio,
            imagen: fresh.imagen || it.imagen,
            producto_id: fresh.id || it.producto_id,
          });
          changed = true;
        } else {
          items.push({
            ...it,
            nombre: fresh.nombre || it.nombre,
            imagen: fresh.imagen || it.imagen,
            producto_id: fresh.id || it.producto_id,
          });
        }
      }
      if (!changed) return state;
      return {
        ...state,
        items,
        _reconcile: { priceChanges, removed, at: Date.now() },
      };
    }
    case 'CLEAR_RECONCILE_NOTICE':
      if (!state._reconcile) return state;
      return { ...state, _reconcile: null };
    case 'HYDRATE_FROM_SAVED': {
      const payload = action.payload || {};
      const meta = action.pedidoMeta || {};
      const clienteData = action.cliente || {};
      const items = (payload.items || action.items || []).map((it) => ({
        lineId: nextLineId(),
        producto_id: it.producto_id || null,
        codigo: it.codigo,
        nombre: it.nombre,
        precio_unitario: Number(it.precio_unitario) || 0,
        imagen: it.imagen || null,
        cantidad: Math.max(1, Number(it.cantidad) || 1),
        observaciones: it.observaciones || '',
      }));
      return {
        ...state,
        items,
        pedidoMeta: {
          ...initialState.pedidoMeta,
          transportadora: meta.transportadora || payload.transportadora || '',
          transportadora_otra: meta.transportadora_otra || payload.transportadora_otra || '',
          horario_recibo: meta.horario_recibo || payload.horario_recibo || '',
          tipo_documento: meta.tipo_documento || payload.tipo_documento || 'remision',
          forma_pago: meta.forma_pago || payload.forma_pago || 'contado',
        },
        cliente: {
          ...initialState.cliente,
          vendedor: state.cliente.vendedor,
          vendedor_id: state.cliente.vendedor_id,
          ...clienteData,
          cliente_id: clienteData.cliente_id ?? payload.cliente_id ?? null,
          sucursal_id: clienteData.sucursal_id ?? payload.sucursal_id ?? null,
          sucursal_codigo:
            clienteData.sucursal_codigo || payload.sucursal_codigo || '',
          nombre: clienteData.nombre || payload.cliente_nombre || '',
          telefono: clienteData.telefono || payload.cliente_telefono || '',
          direccion: clienteData.direccion || payload.sucursal_direccion || '',
          notas: clienteData.notas || payload.notas || '',
          vendedor: clienteData.vendedor || payload.vendedor || state.cliente.vendedor,
          vendedor_id:
            clienteData.vendedor_id || payload.vendedor_id || state.cliente.vendedor_id,
          // No reabrir visita al editar un guardado
          visita_id: null,
          visita_inicio: null,
          visita_fin: null,
        },
      };
    }
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, loadPersistedCart);

  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(state));
    } catch {
      /* quota / private mode */
    }
  }, [state]);

  const totalItems = useMemo(
    () => state.items.reduce((s, i) => s + i.cantidad, 0),
    [state.items]
  );

  const subtotal = useMemo(
    () => state.items.reduce((s, i) => s + i.precio_unitario * i.cantidad, 0),
    [state.items]
  );

  const value = useMemo(
    () => ({ state, dispatch, totalItems, subtotal }),
    [state, totalItems, subtotal]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart debe usarse dentro de CartProvider');
  return ctx;
}
